import { QuickFiltersPanel, QuickFiltersHeader, QuickFiltersRow, AppliedFiltersRow } from "../../layouts/QuickFilters.jsx";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowDown, ArrowUp, ChevronsUpDown, Download } from "lucide-react";
import { FaTimes } from "react-icons/fa";
import server from "../../../api/server.js";
import "../../../dashboard-components/loader.css";
import ModernTimeDropdown from "../../layouts/modernDropdown.jsx";
import ModernDateRangePicker from "../../layouts/ModernDateRangePicker.jsx";
import { useToast } from "../../../context/ToastContext.jsx";
import EventDetails from "./EventDetails.jsx";

const PAGE_SIZE = 50;
const EMPTY_FILTERS = {
  event_reference_id: "",
  customer_id: "",
  event_id: [],
  event_status: [],
  event_time: "",
  start_date: "",
  end_date: "",
};
const EVENT_TIME_OPTIONS = [
  "Today",
  "Yesterday",
  "This Week",
  "This Month",
  "Last Month",
  "Last 7 Days",
  "Last 30 Days",
  "Last 3 Months",
  "Custom Range",
];
const EMPTY_SORT = { field: null, direction: null };

const getCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match ? match[1] : null;
};

// Presets use the same backend date calculation as Conversations and Customers.
const getEventTimeRange = (filters) => {
  if (filters.event_time === "Custom Range") {
    return filters.start_date && filters.end_date
      ? { date_preset: "Custom", start_date: filters.start_date, end_date: filters.end_date }
      : {};
  }
  return filters.event_time ? { date_preset: filters.event_time } : {};
};

const buildRequestFilters = (filters) => ({
  event_reference_id: filters.event_reference_id,
  customer_id: filters.customer_id,
  event_id: filters.event_id,
  event_status: filters.event_status,
  ...getEventTimeRange(filters),
});

// Same time format as the Conversations table.
const formatEpochToReadableDate = (epoch) => {
  if (!epoch) return "";
  const date = new Date(epoch * 1000);
  const options = { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false };
  return date.toLocaleString("en-GB", options).replace(",", "");
};

const sortArray = (list, field, direction) => {
  if (!field || !direction) return list;
  const factor = direction === "asc" ? 1 : -1;
  return [...list].sort((a, b) => {
    const va = typeof a[field] === "string" ? a[field].toLowerCase() : a[field];
    const vb = typeof b[field] === "string" ? b[field].toLowerCase() : b[field];
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    if (typeof va === "number" && typeof vb === "number") return factor * (va - vb);
    return factor * String(va).localeCompare(String(vb));
  });
};

const EventRecords = ({ serviceID, clientID }) => {
  const { showToast } = useToast();
  const [selectedEventReferenceId, setSelectedEventReferenceId] = useState(null);
  const detailsTriggerRef = useRef(null);
  const [email] = useState(() => {
    try {
      const user = getCookie("dashboard_login_user_creds");
      return user ? JSON.parse(decodeURIComponent(user)).email : null;
    } catch (error) {
      return null;
    }
  });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [filterOptions, setFilterOptions] = useState({ events: [], event_status: [] });
  const [records, setRecords] = useState([]);
  const [errorCases, setErrorCases] = useState([]);
  const [showErrorCases, setShowErrorCases] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [sortConfig, setSortConfig] = useState(EMPTY_SORT);
  const [errorSortConfig, setErrorSortConfig] = useState(EMPTY_SORT);
  const [loading, setLoading] = useState(false);
  const [schedulingDownload, setSchedulingDownload] = useState(false);

  const eventsAbortRef = useRef(null);

  const totalPages = Math.max(Math.ceil(totalRecords / PAGE_SIZE), 1);

  const getEventRecords = useCallback(async () => {
    if (!clientID || !serviceID || !email) return;
    if (eventsAbortRef.current) eventsAbortRef.current.abort();
    const controller = new AbortController();
    eventsAbortRef.current = controller;
    try {
      setLoading(true);
      const body = {
        client_id: clientID,
        service_id: serviceID,
        email,
        menu: "events",
        page,
        filters: buildRequestFilters(appliedFilters),
      };
      const res = showErrorCases
        ? await server.getClientEventErrorCases(body, controller.signal)
        : await server.getClientEventRecords(body, controller.signal);
      const resp = await res.json();
      if (controller.signal.aborted) return;
      if (res.status === 200) {
        if (showErrorCases) {
          setErrorCases(resp.data || []);
        } else {
          setRecords(resp.data || []);
          setFilterOptions(resp.filter_options || { events: [], event_status: [] });
        }
        setTotalRecords(resp.total_records || 0);
      } else {
        showToast(resp.error || "Unable to load events", "error");
      }
    } catch (error) {
      if (error.name === "AbortError") return;
      showToast("Unable to load events", "error");
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [clientID, serviceID, email, page, appliedFilters, showErrorCases, showToast]);

  useEffect(() => {
    getEventRecords();
    return () => eventsAbortRef.current?.abort();
  }, [getEventRecords]);

  useEffect(() => {
    setSelectedEventReferenceId(null);
    setRecords([]);
    setErrorCases([]);
    setTotalRecords(0);
    setFilterOptions({ events: [], event_status: [] });
    setPage(1);
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
  }, [clientID, serviceID]);

  // Text filters apply after typing pauses; dropdowns and dates apply immediately.
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setAppliedFilters((prev) => (JSON.stringify(prev) === JSON.stringify(filters) ? prev : filters));
      setPage(1);
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [filters]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleShowErrorCases = (checked) => {
    setShowErrorCases(checked);
    setTotalRecords(0);
    setPage(1);
  };

  const hasActiveFilters = JSON.stringify(filters) !== JSON.stringify(EMPTY_FILTERS);

  const eventLabels = Object.fromEntries((filterOptions.events || []).map((option) => [option.value, option.label]));
  const formatChipValues = (values) => (values.length > 2 ? `${values.slice(0, 2).join(", ")} +${values.length - 2} more` : values.join(", "));
  const activeFilterChips = [
    filters.event_reference_id && { key: "event_reference_id", label: "Event Reference ID", value: filters.event_reference_id, clear: { event_reference_id: "" } },
    filters.customer_id && { key: "customer_id", label: "Customer ID", value: filters.customer_id, clear: { customer_id: "" } },
    filters.event_id.length > 0 && { key: "event_id", label: "Event", value: formatChipValues(filters.event_id.map((id) => eventLabels[id] || id)), clear: { event_id: [] } },
    filters.event_status.length > 0 && { key: "event_status", label: "Event Status", value: formatChipValues(filters.event_status), clear: { event_status: [] } },
    filters.event_time && (filters.event_time !== "Custom Range" || (filters.start_date && filters.end_date)) && {
      key: "event_time",
      label: "Event Time",
      value: filters.event_time === "Custom Range" ? `${filters.start_date} to ${filters.end_date}` : filters.event_time,
      clear: { event_time: "", start_date: "", end_date: "" },
    },
  ].filter(Boolean);

  // Column sorting follows the Conversations table: asc -> desc -> none, applied to the loaded page.
  const arrowClass = (cfg, field, dir) => (cfg.field === field && cfg.direction === dir ? "text-indigo-600" : "text-gray-300 group-hover:text-gray-400");
  const getAriaSort = (cfg, field) => (cfg.field === field ? (cfg.direction === "asc" ? "ascending" : "descending") : "none");
  const cycleSort = (field, table) => {
    const cfg = table === "error" ? errorSortConfig : sortConfig;
    let nextDir = "asc";
    if (cfg.field === field) {
      nextDir = cfg.direction === "asc" ? "desc" : (cfg.direction === "desc" ? null : "asc");
    }
    const setCfg = table === "error" ? setErrorSortConfig : setSortConfig;
    setCfg(nextDir ? { field, direction: nextDir } : EMPTY_SORT);
  };
  const renderArrows = (cfg, field) => {
    if (cfg.field === field && cfg.direction === "asc") return <ArrowUp className={`h-3 w-3 ${arrowClass(cfg, field, "asc")}`} />;
    if (cfg.field === field && cfg.direction === "desc") return <ArrowDown className={`h-3 w-3 -mt-0.5 ${arrowClass(cfg, field, "desc")}`} />;
    return <ChevronsUpDown className={`h-3 w-3 ${arrowClass(cfg, field, "none")}`} />;
  };

  const displayRecords = useMemo(() => sortArray(records, sortConfig.field, sortConfig.direction), [records, sortConfig]);
  const displayErrorCases = useMemo(() => sortArray(errorCases, errorSortConfig.field, errorSortConfig.direction), [errorCases, errorSortConfig]);

  const scheduleDownload = async () => {
    try {
      setSchedulingDownload(true);
      const res = await server.downloadClientEventRecords({
        client_id: clientID,
        service_id: serviceID,
        email,
        menu: "events",
        filters: buildRequestFilters(filters),
      });
      const resp = await res.json();
      if (res.status === 200) {
        showToast(resp.message || "Download scheduled. You will receive an email shortly.", "success");
      } else {
        showToast(resp.error || "Unable to schedule download", "error");
      }
    } catch (error) {
      showToast("Unable to schedule download", "error");
    }
    setSchedulingDownload(false);
  };

  const closeEventDetails = useCallback(() => {
    setSelectedEventReferenceId(null);
    detailsTriggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (selectedEventReferenceId === null) return;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeEventDetails();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [selectedEventReferenceId, closeEventDetails]);

  const openEventDetails = (eventReferenceId, button) => {
    detailsTriggerRef.current = button;
    setSelectedEventReferenceId(eventReferenceId);
  };

  const headerCellClass = "px-3 uppercase py-3 text-left text-xs font-medium text-gray-700 tracking-wider";
  const headerCellStyle = { background: "white", fontSize: "13px", fontWeight: "400" };
  const bodyCellClass = "px-4 py-3 text-sm";
  const bodyCellStyle = { fontSize: "0.7rem", lineHeight: "1rem" };

  const renderSortableHeader = (label, field, table) => {
    const cfg = table === "error" ? errorSortConfig : sortConfig;
    return (
      <th key={field} scope="col" aria-sort={getAriaSort(cfg, field)} className={headerCellClass} style={headerCellStyle}>
        <button type="button" className="flex items-center group" onClick={() => cycleSort(field, table)}>
          <span>{label}</span>
          <span className="ml-1 flex flex-col leading-none">{renderArrows(cfg, field)}</span>
        </button>
      </th>
    );
  };

  return (
    <div className="py-3 px-5 font-sans bg-gray-50 flex flex-col" style={{ height: "100%" }}>
      <main className="flex-1 min-h-0 flex flex-col">
        <div className="mb-4 shrink-0">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Event Records</h1>
          <p className="text-sm text-gray-500">View every event received from your systems and how it was processed</p>
        </div>

        <QuickFiltersPanel className="shrink-0">
          <QuickFiltersHeader>
            {hasActiveFilters && (
              <button className="text-sm text-indigo-600 hover:text-indigo-800" onClick={() => setFilters(EMPTY_FILTERS)}>
                Clear all
              </button>
            )}
            <button
              onClick={scheduleDownload}
              disabled={schedulingDownload}
              className="flex items-center px-4 py-2 rounded text-sm text-white hover:opacity-90 disabled:opacity-60"
              style={{ backgroundColor: "rgb(79, 70, 229)" }}
            >
              <Download className="h-4 w-4 mr-2" />
              {schedulingDownload ? "Scheduling..." : "Download"}
            </button>
          </QuickFiltersHeader>
          <QuickFiltersRow>
            <input
              value={filters.event_reference_id}
              type="text"
              maxLength={255}
              placeholder="Event Reference ID"
              onChange={(e) => handleFilterChange("event_reference_id", e.target.value)}
              className="px-2 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none global-input"
            />
            <input
              value={filters.customer_id}
              type="text"
              maxLength={500}
              placeholder="Customer ID"
              onChange={(e) => handleFilterChange("customer_id", e.target.value)}
              className="px-2 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none global-input"
            />
            <ModernTimeDropdown placeholder="Event" value={filters.event_id} onChange={(e) => handleFilterChange("event_id", e.target.value)} options={filterOptions.events} isMulti />
            <ModernTimeDropdown placeholder="Event Status" value={filters.event_status} onChange={(e) => handleFilterChange("event_status", e.target.value)} options={filterOptions.event_status} isMulti />
            <ModernTimeDropdown
              placeholder="Event Time"
              value={filters.event_time}
              onChange={(e) => setFilters((prev) => ({ ...prev, event_time: e.target.value, start_date: "", end_date: "" }))}
              options={EVENT_TIME_OPTIONS}
            />
          </QuickFiltersRow>
          {filters.event_time === "Custom Range" && (
            <div className="mb-2 mt-2">
              <ModernDateRangePicker
                startDate={filters.start_date}
                endDate={filters.end_date}
                allowAfter={false}
                maxRangeDays={90}
                placeholder="Select Event Time Range"
                onChange={({ startDate, endDate }) => setFilters((prev) => ({ ...prev, start_date: startDate || "", end_date: endDate || "" }))}
              />
            </div>
          )}
          <AppliedFiltersRow>
              {activeFilterChips.map((chip) => (
                <div key={chip.key} className="flex items-center bg-indigo-50 p-2 rounded" style={{ fontSize: "0.6rem", lineHeight: "0.25rem" }}>
                  <span className="text-indigo-600">{chip.label} - {chip.value}</span>
                  <button className="ml-2 text-gray-600" onClick={() => setFilters((prev) => ({ ...prev, ...chip.clear }))}>
                    <FaTimes />
                  </button>
                </div>
              ))}
          </AppliedFiltersRow>
          <div className="flex items-center justify-end w-full">
            <input type="checkbox" checked={showErrorCases} onChange={(e) => handleShowErrorCases(e.target.checked)} />
            <label className="px-2 py-1.5 text-sm font-medium text-gray-700 focus:outline-none">Show Error Cases</label>
          </div>
        </QuickFiltersPanel>

        {/* Fills the space left under the filters so the table and pagination reach the bottom of the page. */}
        <div className="bg-white rounded-lg shadow-sm flex-1 min-h-0" style={{ minHeight: "50vh", overflowY: "auto" }}>
          {loading ? (
            <div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
              <div className="loader"></div>
              <div style={{ textAlign: "center" }}>Loading <br /> Please Wait</div>
            </div>
          ) : showErrorCases ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead style={{ position: "sticky", top: "0" }}>
                <tr>
                  {renderSortableHeader("Event Reference ID", "event_reference_id", "error")}
                  {renderSortableHeader("Customer ID", "customer_id", "error")}
                  {renderSortableHeader("Status", "status", "error")}
                  {renderSortableHeader("Error Type", "error_type", "error")}
                  {renderSortableHeader("Error Description", "error_description", "error")}
                  {renderSortableHeader("Time", "event_time", "error")}
                  {renderSortableHeader("Processed At", "processed_at", "error")}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {displayErrorCases.map((record) => (
                  <tr key={record.event_reference_id} className="border-b hover:bg-gray-50">
                    <td className={`${bodyCellClass} text-blue-600`} style={bodyCellStyle}>{record.event_reference_id}</td>
                    <td className={bodyCellClass} style={bodyCellStyle}>{record.customer_id || ""}</td>
                    <td className={bodyCellClass} style={bodyCellStyle}>{record.status || "Failed"}</td>
                    <td className={bodyCellClass} style={bodyCellStyle}>{record.error_type || ""}</td>
                    <td className={bodyCellClass} style={bodyCellStyle}>{record.error_description || ""}</td>
                    <td className={bodyCellClass} style={bodyCellStyle}>{formatEpochToReadableDate(record.event_time)}</td>
                    <td className={bodyCellClass} style={bodyCellStyle}>{formatEpochToReadableDate(record.processed_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead style={{ position: "sticky", top: "0" }}>
                <tr>
                  {renderSortableHeader("Event Reference ID", "event_reference_id", "main")}
                  {renderSortableHeader("Event Time", "event_time", "main")}
                  {renderSortableHeader("Customer ID", "customer_id", "main")}
                  {renderSortableHeader("Event", "event_name", "main")}
                  {renderSortableHeader("Event Status", "event_status", "main")}
                  {renderSortableHeader("Received At", "received_at", "main")}
                  <th scope="col" className={headerCellClass} style={headerCellStyle}>Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {displayRecords.length === 0 && (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center text-sm text-gray-500">No events found.</td>
                  </tr>
                )}
                {displayRecords.map((record) => (
                  <tr key={record.event_reference_id} className="border-b hover:bg-gray-50">
                    <td className={`${bodyCellClass} text-blue-600`} style={bodyCellStyle}>{record.event_reference_id}</td>
                    <td className={bodyCellClass} style={bodyCellStyle}>{formatEpochToReadableDate(record.event_time)}</td>
                    <td className={bodyCellClass} style={bodyCellStyle}>{record.customer_id}</td>
                    <td className={bodyCellClass} style={bodyCellStyle}>{record.event_name}</td>
                    <td className={bodyCellClass} style={bodyCellStyle}>{record.event_status}</td>
                    <td className={bodyCellClass} style={bodyCellStyle}>{formatEpochToReadableDate(record.received_at)}</td>
                    <td className={bodyCellClass} style={bodyCellStyle}>
                      <button type="button" onClick={(event) => openEventDetails(record.event_reference_id, event.currentTarget)} className="text-blue-600">View Details</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="shrink-0 bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <p className="text-sm text-gray-700">
            Showing <span className="font-medium">{totalRecords === 0 ? 0 : PAGE_SIZE * (page - 1) + 1}</span> to <span className="font-medium">{Math.min(PAGE_SIZE * page, totalRecords)}</span> of <span className="font-medium">{totalRecords}</span> records
          </p>
          <nav className="z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            <button
              onClick={() => setPage((current) => Math.max(current - 1, 1))}
              disabled={page <= 1 || loading}
              className="inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((current) => Math.min(current + 1, totalPages))}
              disabled={page >= totalPages || loading}
              className="inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </nav>
        </div>
      </main>
      {selectedEventReferenceId !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50" style={{ zIndex: 1001 }}>
          <div role="dialog" aria-modal="true" aria-label="Event details" className="fixed bg-white rounded-lg shadow-sm" style={{ left: "2%", top: "3.5%", width: "96%", height: "93%" }}>
            <button type="button" autoFocus aria-label="Close event details" onClick={closeEventDetails} className="absolute right-2.5 top-2.5 text-slate-500 z-10">
              <FaTimes />
            </button>
            <div className="h-full overflow-auto rounded-lg">
              <EventDetails selectedOption={serviceID} selectedClientId={clientID} eventReferenceIdProp={selectedEventReferenceId} onClose={closeEventDetails} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventRecords;
